import { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';

import { Box, Grid, MenuItem } from '@material-ui/core';
import { ButtonControlGroup } from '../../../helpers/ButtonControlGroup';

import { useIntl } from '../../../../../../_metronic/i18n/customUseIntl';
import RowTextFieldWithInfo from '../../../../../modules/Farms/Intervention/RowTextFieldWithInfo';
import InfoToolTip from '../../../../../modules/Helpers/InfoToolTip';
import ReactHookFormSelect from '../../../../../modules/Helpers/ReactHookFormSelect';
import lookupValues from '../../../../helpers/lookupValues';
import { objectEntriesSorter } from '../../../../helpers/sortingFunctions';
import { InterventionInfo } from '../../../../models/Intervention';
import { DialogContainer } from '../../CommonDataParts/DialogContainer';
import { FormType } from '../../common';
import { GenericInterventionDialogProps } from './InterventionTypes';

export const isFieldFilled = (fieldValue: any): boolean => {
  if (typeof fieldValue === 'object') {
    const value = fieldValue.value;
    return value !== null && value !== undefined && value !== "";
  }

  return fieldValue !== undefined && fieldValue !== "";
}

const InterventionGenericInfoDialog = ({
  formVisible,
  formType = FormType.Add,
  handleCancel,
}: GenericInterventionDialogProps) => {
  const intl = useIntl();
  const currResetValue = useRef<InterventionInfo>();

  const formTitle = intl.formatMessage({ id: 'GENERAL.PROFILE' });
  const fieldPrefix = 'info';
  const fc = useFormContext();

  useEffect(() => {
    if (formVisible) {
      currResetValue.current = { ...fc.getValues('info') } as InterventionInfo; // must 'spread' otherwise just reference
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formVisible]);

  const handleResetClick = () => {
    // console.log("resetValue:", currResetValue.current);
    if (currResetValue.current) {
      fc.reset(
        {
          ...fc.getValues(),
          info: currResetValue.current, // replace whatever exists in the current form values
        },
        {
          errors: true,
        }
      );
    }
    handleCancel();
  };
  return (
    <DialogContainer
      formVisible={formVisible}
      handleClose={handleCancel}
      iconCode="info"
      formTitle={formTitle}
    >
      {formType === 'edit' && (
        <input
          ref={fc.register()}
          type="hidden"
          name={`${fieldPrefix}.oldName`}
        />
      )}
      <Grid container direction="column" spacing={2} justifyContent="space-between">
        <Grid item xs={12}>
          <RowTextFieldWithInfo
            control={fc.control}
            name={`${fieldPrefix}.name`}
            label={`${intl.formatMessage({ id: 'INTERVENTION.FORM.NAME' })} *`}
            margin="none"
            variant="outlined"
            disabled={formType === 'view'}
          />
        </Grid>
        <Grid item container xs={12}>
          <Grid item xs={11}>
            <ReactHookFormSelect
              name={`${fieldPrefix}.interventionCategory`}
              label={intl.formatMessage({ id: 'INTERVENTION.FORM.CATEGORY' })}
              control={fc.control}
              variant="outlined"
              // onBlur={fc.onBlur}
              fullWidth
              disabled={formType === 'view'}
            >
              <MenuItem key="None" value="">
                <em>{intl.formatMessage({ id: 'GENERAL.NONE' })}</em>
              </MenuItem>
              {Object.entries(lookupValues.interventionCategories)
                .sort(objectEntriesSorter)
                .map(([i, item]) => (
                  <MenuItem key={i} value={i}>
                    {item}
                  </MenuItem>
                ))}
            </ReactHookFormSelect>
          </Grid>

          <Grid item xs={1} className="d-flex">
            <Box pt="15px" pl="15px">
              <InfoToolTip
                tooltip={intl.formatMessage({
                  id: 'INTERVENTION.FORM.CATEGORY.TOOLTIP',
                })}
              />
            </Box>
          </Grid>
        </Grid>

        <Grid item>
          <RowTextFieldWithInfo
            control={fc.control}
            name={`${fieldPrefix}.description`}
            label={`${intl.formatMessage({
              id: 'INTERVENTION.FORM.DESCRIPTION',
            })} *`}
            margin="none"
            variant="outlined"
            tooltip={intl.formatMessage({
              id: 'INTERVENTION.FORM.DESCRIPTION.TOOLTIP',
            })}
            multiline
            disabled={formType === 'view'}
          />
        </Grid>

        <Grid item xs={11}>
          <ButtonControlGroup
            cancelHandler={handleResetClick}
            saveHandler={handleCancel}
            saveLabel={intl.formatMessage({ id: 'GENERAL.CONFIRM' })}
          />
        </Grid>
      </Grid>
    </DialogContainer>
  );
};

export default InterventionGenericInfoDialog;
